import { collection, addDoc, query, orderBy, limit, where, getDocs } from 'firebase/firestore';
import { db } from './config';

export interface ActivityLog {
  id: string;
  userId: string;
  userName: string;
  userHandle?: string;
  action: string;
  details: string;
  category: 'user' | 'post' | 'event' | 'message' | 'admin' | 'system';
  severity: 'low' | 'medium' | 'high' | 'critical';
  metadata?: {
    postId?: string;
    eventId?: string;
    messageId?: string;
    targetUserId?: string;
    ipAddress?: string;
    userAgent?: string;
    path?: string;
  };
  timestamp: Date;
}

// Log user activity
export const logActivity = async (activity: Omit<ActivityLog, 'id' | 'timestamp'>): Promise<string> => {
  try {
    const cleanedActivity = Object.entries(activity).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        (acc as any)[key] = value;
      }
      return acc;
    }, {} as Omit<ActivityLog, 'id' | 'timestamp'>);

    const docRef = await addDoc(collection(db, 'activityLogs'), {
      ...cleanedActivity,
      timestamp: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error logging activity:', error);
    throw error;
  }
};

// Get recent activity logs
export const getRecentActivity = async (limitCount: number = 50): Promise<ActivityLog[]> => {
  try {
    const q = query(
      collection(db, 'activityLogs'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as ActivityLog));
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    return [];
  }
};

export const getActivitySince = async (since: Date): Promise<ActivityLog[]> => {
  try {
    const q = query(
      collection(db, 'activityLogs'),
      where('timestamp', '>=', since),
      orderBy('timestamp', 'asc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ActivityLog));
  } catch (error) {
    console.error('Error fetching activity since:', error);
    return [];
  }
};

// Get activity logs by user
export const getUserActivity = async (userId: string, limitCount: number = 20): Promise<ActivityLog[]> => {
  try {
    const q = query(
      collection(db, 'activityLogs'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as ActivityLog));
  } catch (error) {
    console.error('Error fetching user activity:', error);
    return [];
  }
};

// Get activity logs by severity
export const getActivityBySeverity = async (severity: 'low' | 'medium' | 'high' | 'critical'): Promise<ActivityLog[]> => {
  try {
    const q = query(
      collection(db, 'activityLogs'),
      where('severity', '==', severity),
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as ActivityLog));
  } catch (error) {
    console.error('Error fetching activity by severity:', error);
    return [];
  }
};

// Helper functions for common activities
export const logUserRegistration = async (userId: string, userName: string, userHandle?: string) => {
  return logActivity({
    userId,
    userName,
    userHandle,
    action: 'User Registration',
    details: `${userName} registered for the platform`,
    category: 'user',
    severity: 'low'
  });
};

export const logUserLogin = async (userId: string, userName: string, userHandle?: string) => {
  return logActivity({
    userId,
    userName,
    userHandle,
    action: 'User Login',
    details: `${userName} logged into the platform`,
    category: 'user',
    severity: 'low'
  });
};

export const logPageView = async (
  path: string,
  userId?: string,
  userName?: string,
  userHandle?: string
) => {
  return logActivity({
    userId: userId || 'anonymous',
    userName: userName || 'Anonymous',
    userHandle,
    action: 'Page View',
    details: `Viewed ${path}`,
    category: 'system',
    severity: 'low',
    metadata: { path, userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined }
  });
};

export const logPostCreation = async (userId: string, userName: string, postId: string, postTitle: string) => {
  return logActivity({
    userId,
    userName,
    action: 'Post Created',
    details: `${userName} created a new post: "${postTitle}"`,
    category: 'post',
    severity: 'low',
    metadata: { postId }
  });
};

export const logEventCreation = async (userId: string, userName: string, eventId: string, eventTitle: string) => {
  return logActivity({
    userId,
    userName,
    action: 'Event Created',
    details: `${userName} created a new event: "${eventTitle}"`,
    category: 'event',
    severity: 'low',
    metadata: { eventId }
  });
};

export const logMessageSent = async (userId: string, userName: string, messageId: string, recipientId: string) => {
  return logActivity({
    userId,
    userName,
    action: 'Message Sent',
    details: `${userName} sent a message`,
    category: 'message',
    severity: 'low',
    metadata: { messageId, targetUserId: recipientId }
  });
};

export const logAdminAction = async (adminId: string, adminName: string, action: string, details: string, severity: 'low' | 'medium' | 'high' | 'critical' = 'medium') => {
  return logActivity({
    userId: adminId,
    userName: adminName,
    action: `Admin: ${action}`,
    details,
    category: 'admin',
    severity
  });
};

export const logSuspiciousActivity = async (userId: string, userName: string, action: string, details: string) => {
  return logActivity({
    userId,
    userName,
    action: `Suspicious: ${action}`,
    details,
    category: 'system',
    severity: 'high'
  });
};

export const logUserBan = async (adminId: string, adminName: string, bannedUserId: string, bannedUserName: string, reason: string) => {
  return logActivity({
    userId: adminId,
    userName: adminName,
    action: 'User Banned',
    details: `${adminName} banned ${bannedUserName}. Reason: ${reason}`,
    category: 'admin',
    severity: 'high',
    metadata: { targetUserId: bannedUserId }
  });
};

export const logUserWarn = async (adminId: string, adminName: string, warnedUserId: string, warnedUserName: string, reason: string) => {
  return logActivity({
    userId: adminId,
    userName: adminName,
    action: 'User Warned',
    details: `${adminName} warned ${warnedUserName}. Reason: ${reason}`,
    category: 'admin',
    severity: 'medium',
    metadata: { targetUserId: warnedUserId }
  });
};
